import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval,
         getDay, addMonths, subMonths, isSameMonth } from 'date-fns';
import { ru } from 'date-fns/locale';

const DAYS_RU = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

function scoreToClass(score, blocked) {
  if (blocked) return 'bad';
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'neutral';
  if (score >= 20) return 'poor';
  return 'bad';
}

const COLOR = {
  excellent: { bg: 'rgba(45,122,45,0.25)',   border: '#2d7a2d', text: '#5cb85c' },
  good:      { bg: 'rgba(92,184,92,0.15)',   border: '#5cb85c', text: '#7ecb7e' },
  neutral:   { bg: 'rgba(240,173,78,0.15)',  border: '#f0ad4e', text: '#f0ad4e' },
  poor:      { bg: 'rgba(230,126,34,0.12)',  border: '#e67e22', text: '#e67e22' },
  bad:       { bg: 'rgba(192,57,43,0.12)',   border: '#c0392b', text: '#e74c3c' },
};

export default function CalendarPage({ onLogout, mode = 'paid' }) {
  const guest = mode !== 'paid';
  const navigate = useNavigate();
  const [days, setDays] = useState([]);
  const [topDays, setTopDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [viewMonth, setViewMonth] = useState(new Date());
  const [form, setForm] = useState(null);
  const [stats, setStats] = useState({ total: 0, favorable: 0 });

  useEffect(() => {
    if (mode === 'demo') {
      setForm({ client_name: 'Пример: Анна · 12.03.1985 · Днепр' });
      api.demo().then(({ data }) => {
        setDays(data.days); setTopDays(data.top_days);
        setStats({ total: data.total_days, favorable: data.favorable_days });
        setViewMonth(new Date(data.period_start));
      }).catch(() => setError('Не удалось загрузить пример. Проверьте интернет.')).finally(() => setLoading(false));
      return;
    }
    const raw = sessionStorage.getItem('calcForm');
    if (!raw) { navigate(mode === 'free' ? '/free' : '/dashboard'); return; }
    const f = JSON.parse(raw);
    setForm(f);
    setViewMonth(new Date(f.start_date));
    fetchData(f);
  }, []);

  const fetchData = async (f) => {
    setLoading(true);
    try {
      const call = mode === 'free' ? api.calculateFree : api.calculate;
      const { data } = await call({
        birth_date:  f.birth_date,
        birth_time:  f.birth_time || '12:00',
        birth_lat:   parseFloat(f.birth_lat),
        birth_lon:   parseFloat(f.birth_lon),
        utc_offset:  parseFloat(f.utc_offset),
        start_date:  f.start_date,
        end_date:    f.end_date,
      });
      setDays(data.days);
      setTopDays(data.top_days);
      setStats({ total: data.total_days, favorable: data.favorable_days });
    } catch (e) {
      const st = e.response?.status;
      setError(st === 403 || st === 429
        ? (e.response?.data?.detail || 'Бесплатное сканирование недоступно. Зарегистрируйтесь, чтобы продолжить.')
        : (e.response?.data?.detail || 'Ошибка расчёта. Проверьте данные.'));
    } finally {
      setLoading(false);
    }
  };

  const dayMap = Object.fromEntries(days.map(d => [d.date, d]));

  const downloadPdf = async () => {
    if (!form) return;
    try {
      const { data } = await api.generatePdf({
        client_name: form.client_name || form.name || 'Клиент',
        birth_date:  form.birth_date,
        birth_time:  form.birth_time || '12:00',
        birth_lat:   parseFloat(form.birth_lat),
        birth_lon:   parseFloat(form.birth_lon),
        utc_offset:  parseFloat(form.utc_offset),
        start_date:  form.start_date,
        end_date:    form.end_date,
        agency_name: 'AstraData',
      });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `astrodata_${form.name}_${form.start_date}.pdf`;
      a.click();
    } catch { alert('Ошибка генерации PDF'); }
  };

  // Сетка календаря для текущего месяца
  const monthDays = (() => {
    const start = startOfMonth(viewMonth);
    const end   = endOfMonth(viewMonth);
    const allDays = eachDayOfInterval({ start, end });
    // Сдвиг: понедельник = 0
    const offset = (getDay(start) + 6) % 7;
    return { allDays, offset };
  })();

  if (loading) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16}}>
      <div style={styles.spinner} />
      <p style={{color:'#a78bfa',fontSize:16}}>Считаем звёзды...</p>
      <p style={{color:'#555570',fontSize:13}}>Анализируем транзиты, Панчангу и Дашу</p>
    </div>
  );

  return (
    <div style={styles.page}>
      {/* Шапка */}
      <header style={styles.header}>
        <button className="btn btn-outline" onClick={() => navigate(mode === 'paid' ? '/dashboard' : (mode === 'free' ? '/free' : '/'))}
          style={{padding:'10px 14px',fontSize:14,minHeight:44}}>← Назад</button>
        <div style={styles.logo}>✦ AstraData</div>
        {guest ? (
          <button onClick={() => navigate('/')} aria-label="Закрыть"
            style={{width:44,height:44,borderRadius:'50%',border:'1px solid #3a3a5c',background:'#22223a',color:'#fff',fontSize:22,cursor:'pointer'}}>✕</button>
        ) : (
          <button className="btn btn-outline" onClick={onLogout}
            style={{padding:'8px 14px',fontSize:13}}>Выйти</button>
        )}
      </header>

      <main style={styles.main}>
        {error && <div style={styles.errorBox}>{error}</div>}

        {/* Сводка */}
        <div style={styles.summary}>
          <h1 style={styles.clientName}>{form?.client_name || form?.name || 'Клиент'}</h1>
          <div style={styles.statsRow}>
            <div style={styles.statBox}>
              <div style={styles.statNum}>{stats.total}</div>
              <div style={styles.statLabel}>Всего дней</div>
            </div>
            <div style={{...styles.statBox, borderColor:'#2d7a2d'}}>
              <div style={{...styles.statNum, color:'#5cb85c'}}>{stats.favorable}</div>
              <div style={styles.statLabel}>Благоприятных</div>
            </div>
            <div style={{...styles.statBox, borderColor:'#c0392b'}}>
              <div style={{...styles.statNum, color:'#e74c3c'}}>
                {days.filter(d => d.is_blocked).length}
              </div>
              <div style={styles.statLabel}>Избегать</div>
            </div>
          </div>
          {mode === 'demo' && (
            <div className="card" style={{border:'1px solid #c9a84c',background:'rgba(201,168,76,0.08)',width:'100%'}}>
              <h3 style={{marginBottom:6}}>Так выглядит результат</h3>
              <p style={{fontSize:14,color:'#c8bfa8',lineHeight:1.6}}>
                Это настоящий расчёт для примера: родилась 12.03.1985 в Днепре, период — ближайшие 45 дней.
                Каждому дню приложение ставит оценку от 0 до 100: зелёные — лучшие дни для подписания договора,
                задатка или заселения; красные — дни, которых лучше избегать. Нажмите на любой день — увидите,
                из чего сложилась оценка. Ниже — пять лучших дат периода.
              </p>
              <button className="btn btn-gold" style={{marginTop:12,width:'100%',whiteSpace:'normal',textAlign:'center'}} onClick={() => navigate('/free')}>Рассчитать для моей даты — бесплатно</button>
            </div>
          )}
          {guest ? (
            <button className="btn btn-outline" onClick={() => navigate('/register')} style={{marginTop:4}}>
              🔒 PDF-отчёт — в полной версии
            </button>
          ) : (
            <button className="btn btn-primary" onClick={downloadPdf} style={{marginTop:4}}>
              📄 Скачать PDF-отчёт
            </button>
          )}
        </div>

        {/* Топ-5 лучших дней */}
        {topDays.length > 0 && (
          <div style={styles.topSection}>
            <h2 style={styles.sectionTitle}>⭐ Топ-5 лучших дней</h2>
            <div style={styles.topGrid}>
              {topDays.map((d, i) => (
                <div key={d.date} style={styles.topCard} onClick={() => setSelected(d)}>
                  <div style={styles.topRank}>#{i+1}</div>
                  <div style={styles.topDate}>
                    {format(parseISO(d.date), 'd MMMM yyyy', { locale: ru })}
                  </div>
                  <div style={{...styles.topScore, color: COLOR[scoreToClass(d.score, d.is_blocked)].text}}>
                    {d.score.toFixed(0)}/100
                  </div>
                  <div style={styles.topDetail}>{d.tithi_name_ru}</div>
                  <div style={styles.topDetail}>{d.nakshatra_name_ru}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Навигация по месяцам + Легенда */}
        <div style={styles.calHeader}>
          <div style={styles.monthNav}>
            <button className="btn btn-outline" onClick={() => setViewMonth(m => subMonths(m,1))}
              style={{padding:'6px 12px'}}>←</button>
            <span style={styles.monthTitle}>
              {format(viewMonth, 'LLLL yyyy', { locale: ru })}
            </span>
            <button className="btn btn-outline" onClick={() => setViewMonth(m => addMonths(m,1))}
              style={{padding:'6px 12px'}}>→</button>
          </div>
          <div style={styles.legend}>
            {Object.entries(COLOR).map(([key, c]) => (
              <div key={key} style={styles.legendItem}>
                <div style={{...styles.legendDot, background: c.border}} />
                <span style={{color: c.text, fontSize: 12}}>
                  {{excellent:'Отличный',good:'Хороший',neutral:'Нейтральный',poor:'Нежелат.',bad:'Избегать'}[key]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Сетка календаря */}
        <div className="card" style={{padding:20}}>
          {/* Дни недели */}
          <div style={styles.weekRow}>
            {DAYS_RU.map(d => (
              <div key={d} style={styles.weekDay}>{d}</div>
            ))}
          </div>

          {/* Сетка дней */}
          <div style={styles.grid}>
            {/* Пустые ячейки в начале */}
            {Array.from({length: monthDays.offset}).map((_, i) => (
              <div key={`e${i}`} />
            ))}

            {monthDays.allDays.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const d = dayMap[dateStr];
              if (!d) return <div key={dateStr} style={styles.dayEmpty} />;

              const cls = scoreToClass(d.score, d.is_blocked);
              const c   = COLOR[cls];
              const isSelected = selected?.date === dateStr;

              return (
                <div key={dateStr}
                  onClick={() => setSelected(d)}
                  style={{
                    ...styles.dayCell,
                    background: c.bg,
                    border: `1.5px solid ${isSelected ? '#a78bfa' : c.border}`,
                    boxShadow: isSelected ? '0 0 0 2px #a78bfa' : 'none',
                    cursor: 'pointer',
                  }}>
                  <div style={styles.dayNum}>{format(day, 'd')}</div>
                  <div style={{...styles.dayScore, color: c.text}}>
                    {d.is_blocked ? '✕' : d.score.toFixed(0)}
                  </div>
                  <div style={styles.dayTithi}>{d.tithi_num}T</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Карточка выбранного дня */}
        {selected && (
          <div className="card" style={styles.dayCard}>
            <div style={styles.dayCardHeader}>
              <div>
                <h2 style={styles.dayCardTitle}>
                  {format(parseISO(selected.date), 'EEEE, d MMMM yyyy', { locale: ru })}
                </h2>
                <span className={`badge badge-${scoreToClass(selected.score, selected.is_blocked)}`}>
                  {selected.label_ru} — {selected.score.toFixed(0)}/100
                </span>
              </div>
              <button onClick={() => setSelected(null)} style={styles.closeBtn}>✕</button>
            </div>

            {selected.is_blocked && (
              <div style={styles.blockAlert}>
                ⛔ {selected.block_reason}
              </div>
            )}

            {/* Панчанга */}
            <h3 style={styles.cardSection}>Панчанга</h3>
            <div style={styles.panchangaGrid}>
              {[
                { label: 'Титхи',     val: selected.tithi_name_ru },
                { label: 'Накшатра',  val: selected.nakshatra_name_ru },
                { label: 'День',      val: selected.vara_name_ru },
                { label: 'Йога',      val: selected.yoga_name_ru },
                { label: 'Карана',    val: selected.karana_name_ru },
              ].map(item => (
                <div key={item.label} style={styles.panchangaItem}>
                  <div style={styles.panchangaLabel}>{item.label}</div>
                  <div style={styles.panchangaVal}>{item.val}</div>
                </div>
              ))}
            </div>

            {/* Даша */}
            <h3 style={styles.cardSection}>Вимшоттари Даша</h3>
            <div style={styles.panchangaGrid}>
              <div style={styles.panchangaItem}>
                <div style={styles.panchangaLabel}>Маха-Даша</div>
                <div style={styles.panchangaVal}>{selected.maha_dasha}</div>
              </div>
              <div style={styles.panchangaItem}>
                <div style={styles.panchangaLabel}>Антар-Даша</div>
                <div style={styles.panchangaVal}>{selected.antar_dasha}</div>
              </div>
              {selected.retro_planets?.length > 0 && (
                <div style={styles.panchangaItem}>
                  <div style={styles.panchangaLabel}>Ретро</div>
                  <div style={{...styles.panchangaVal, color:'#e74c3c'}}>
                    {selected.retro_planets.join(', ')}
                  </div>
                </div>
              )}
            </div>

            {/* Плюсы и минусы */}
            {selected.top_positive?.length > 0 && (
              <>
                <h3 style={styles.cardSection}>Благоприятные факторы</h3>
                <ul style={styles.factorList}>
                  {selected.top_positive.map((f, i) => (
                    <li key={i} style={{...styles.factorItem, color:'#5cb85c'}}>✓ {f}</li>
                  ))}
                </ul>
              </>
            )}
            {selected.top_negative?.length > 0 && (
              <>
                <h3 style={styles.cardSection}>Неблагоприятные факторы</h3>
                <ul style={styles.factorList}>
                  {selected.top_negative.map((f, i) => (
                    <li key={i} style={{...styles.factorItem, color:'#e74c3c'}}>✕ {f}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {guest && !loading && !error && (
          <div className="card" style={{border:'1px solid #c9a84c',background:'rgba(201,168,76,0.08)',textAlign:'center'}}>
            <h3 style={{marginBottom:8}}>{mode === 'free' ? 'Ваше бесплатное сканирование готово' : 'Хотите такой результат для себя?'}</h3>
            <p style={{fontSize:14,color:'#c8bfa8',lineHeight:1.6,marginBottom:14}}>
              {mode === 'free'
                ? 'Создайте кабинет, чтобы считать без ограничений, получать PDF-отчёт с лучшими датами и ИИ-объяснение каждого дня простым языком.'
                : 'Первое сканирование — бесплатно и без регистрации. Дальше — один раз оплатили и считаете без ограничений.'}
            </p>
            <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
              {mode === 'demo'
                ? <button className="btn btn-gold" onClick={() => navigate('/free')}>Бесплатное сканирование</button>
                : <button className="btn btn-gold" onClick={() => navigate('/register')}>Создать кабинет</button>}
              <button className="btn btn-outline" onClick={() => navigate('/register')}>Тарифы и регистрация</button>
            </div>
          </div>
        )}
        {guest && error && (
          <div style={{textAlign:'center'}}>
            <button className="btn btn-gold" onClick={() => navigate('/register')}>Зарегистрироваться</button>
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  page:           { minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  header:         { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', borderBottom: '1px solid #2a2a4a', background: '#0f0f1a' },
  logo:           { fontSize: 18, fontWeight: 700, color: '#a78bfa' },
  main:           { flex: 1, maxWidth: 960, margin: '0 auto', padding: '32px 16px', width: '100%', display: 'flex', flexDirection: 'column', gap: 24 },
  errorBox:       { background: 'rgba(192,57,43,0.15)', border: '1px solid #c0392b', borderRadius: 10, padding: 14, color: '#e74c3c', fontSize: 14 },
  spinner:        { width: 48, height: 48, border: '3px solid #2a2a4a', borderTop: '3px solid #a78bfa', borderRadius: '50%', animation: 'spin 1s linear infinite' },

  summary:        { display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' },
  clientName:     { fontSize: 26, fontWeight: 700 },
  statsRow:       { display: 'flex', gap: 12 },
  statBox:        { background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 12, padding: '12px 20px', textAlign: 'center' },
  statNum:        { fontSize: 28, fontWeight: 700, color: '#a78bfa' },
  statLabel:      { fontSize: 12, color: '#666680', marginTop: 2 },

  topSection:     {},
  sectionTitle:   { fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#a0a0c0' },
  topGrid:        { display: 'flex', gap: 10, flexWrap: 'wrap' },
  topCard:        { background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 12, padding: '14px 16px', cursor: 'pointer', minWidth: 150, transition: 'border-color 0.2s', flex: '1 1 150px' },
  topRank:        { fontSize: 12, color: '#a78bfa', fontWeight: 700, marginBottom: 4 },
  topDate:        { fontSize: 13, fontWeight: 600, marginBottom: 4 },
  topScore:       { fontSize: 20, fontWeight: 700, marginBottom: 4 },
  topDetail:      { fontSize: 11, color: '#666680' },

  calHeader:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
  monthNav:       { display: 'flex', alignItems: 'center', gap: 12 },
  monthTitle:     { fontSize: 17, fontWeight: 600, minWidth: 160, textAlign: 'center' },
  legend:         { display: 'flex', gap: 12, flexWrap: 'wrap' },
  legendItem:     { display: 'flex', alignItems: 'center', gap: 5 },
  legendDot:      { width: 10, height: 10, borderRadius: '50%' },

  weekRow:        { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 },
  weekDay:        { textAlign: 'center', fontSize: 12, color: '#555570', fontWeight: 600, padding: '4px 0' },
  grid:           { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
  dayCell:        { borderRadius: 10, padding: '6px 4px', textAlign: 'center', minHeight: 64, transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 },
  dayEmpty:       { minHeight: 64, borderRadius: 10 },
  dayNum:         { fontSize: 15, fontWeight: 700 },
  dayScore:       { fontSize: 13, fontWeight: 600 },
  dayTithi:       { fontSize: 10, color: '#555570' },

  dayCard:        { marginTop: 4 },
  dayCardHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  dayCardTitle:   { fontSize: 18, fontWeight: 600, marginBottom: 6, textTransform: 'capitalize' },
  closeBtn:       { background: 'none', border: 'none', color: '#666680', fontSize: 18, cursor: 'pointer', padding: 4 },
  blockAlert:     { background: 'rgba(192,57,43,0.15)', border: '1px solid #c0392b', borderRadius: 10, padding: '10px 14px', color: '#e74c3c', fontSize: 13, marginBottom: 16 },
  cardSection:    { fontSize: 13, fontWeight: 600, color: '#666680', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 16, marginBottom: 8 },
  panchangaGrid:  { display: 'flex', gap: 8, flexWrap: 'wrap' },
  panchangaItem:  { background: '#12122a', borderRadius: 10, padding: '10px 14px', minWidth: 100, flex: '1 1 100px' },
  panchangaLabel: { fontSize: 11, color: '#555570', marginBottom: 4 },
  panchangaVal:   { fontSize: 13, fontWeight: 600 },
  factorList:     { display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 0, listStyle: 'none' },
  factorItem:     { fontSize: 13, padding: '4px 0' },
};
